import React, { createContext, useContext, useLayoutEffect } from 'react';
import type { EntityUpsertRenderFieldContext } from './EntityUpsertRenderFieldContext';

/** Session value for one entity upsert surface (create/edit/view). Same shape as the former renderField closure. */
export type EntityUpsertSessionValue = EntityUpsertRenderFieldContext;

const EntityUpsertSessionContext = createContext<EntityUpsertSessionValue | null>(null);

/**
 * Ref mirror so {@link createEntityUpsertRenderField} can read the latest session without
 * recreating the render function when formData changes.
 */
export const entityUpsertSessionRef: { current: EntityUpsertSessionValue | null } = { current: null };

export interface EntityUpsertSessionProviderProps {
    value: EntityUpsertSessionValue;
    children: React.ReactNode;
}

export function EntityUpsertSessionProvider({ value, children }: EntityUpsertSessionProviderProps) {
    entityUpsertSessionRef.current = value;

    useLayoutEffect(() => {
        entityUpsertSessionRef.current = value;
        return () => {
            entityUpsertSessionRef.current = null;
        };
    }, [value]);

    return (
        <EntityUpsertSessionContext.Provider value={value}>
            {children}
        </EntityUpsertSessionContext.Provider>
    );
}

export function useEntityUpsertSession(): EntityUpsertSessionValue {
    const session = useContext(EntityUpsertSessionContext);
    if (session == null) {
        throw new Error('useEntityUpsertSession must be used within EntityUpsertSessionProvider');
    }
    return session;
}

/** For non-React render helpers (field factory) — requires an active provider. */
export function getEntityUpsertSession(): EntityUpsertSessionValue {
    const session = entityUpsertSessionRef.current;
    if (session == null) {
        throw new Error('getEntityUpsertSession called without EntityUpsertSessionProvider');
    }
    return session;
}
